import * as XLSX from 'xlsx';
import { useInventoryStore, Item, NewStockUnit, StockUnit } from '../store/inventoryStore';
import { useWarehouseStore, Warehouse, StorageArea } from '../store/warehouseStore';
import { useOwnersStore, Owner } from '../store/ownersStore';
import { supabase } from '../lib/supabase';

export const MAX_IMPORT_ROWS = 2000;
const UNIT_CHUNK_SIZE = 100;

export type ImportKind = 'types' | 'units';
export type TypesImportMode = 'add' | 'replace';
export type DuplicateAction = 'skip' | 'update' | 'add_new';

export interface ParsedTypeRow {
  name: string;
  sku: string | null;
  category: string | null;
  notes: string | null;
}

export interface ParsedUnitRow {
  rowNumber: number;
  name: string;
  sku: string | null;
  category: string | null;
  serialMac: string | null;
  warehouse: string | null;
  storageArea: string | null;
  conditionRaw: string;
  statusRaw: string;
  reference: string | null;
  owner: string | null;
  notes: string | null;
}

export interface RowIssue {
  rowNumber: number;
  name: string;
  message: string;
}

export interface UnitImportPreview {
  validRows: ParsedUnitRow[];
  blocked: RowIssue[];
  warnings: RowIssue[];
  sampleRows: ParsedUnitRow[];
  summary: {
    unitsToCreate: number;
    newItemTypes: string[];
    reusedItemTypes: string[];
    discontinuedItemTypes: string[];
    newWarehouses: string[];
    newStorageAreas: { warehouse: string; area: string }[];
    newOwners: string[];
  };
}

export interface TypesImportResult {
  added: number;
  updated: number;
  skipped: number;
  errors: string[];
}

export interface UnitsImportResult {
  itemsCreated: number;
  warehousesCreated: number;
  areasCreated: number;
  ownersCreated: number;
  unitsCreated: number;
  skipped: number;
  errors: string[];
}

const key = (s: string) => s.trim().toLowerCase();
const cell = (v: unknown) => String(v ?? '').trim();
const emptyToNull = (s: string) => (s.length > 0 ? s : null);

function valuesOf<T>(map: Map<string, T>): T[] {
  const out: T[] = [];
  map.forEach(v => { out.push(v); });
  return out;
}

function setValues<T>(s: Set<T>): T[] {
  const out: T[] = [];
  s.forEach(v => { out.push(v); });
  return out;
}

export function normaliseHeader(s: string) {
  return s.toLowerCase().replace(/[^a-z]/g, '');
}

function readSheetRows(fileData: ArrayBuffer): Record<string, unknown>[] {
  const wb = XLSX.read(new Uint8Array(fileData), { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  return XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: '' });
}

function headerMapFromRow(row: Record<string, unknown>): Map<string, string> {
  const map = new Map<string, string>();
  Object.keys(row).forEach(k => map.set(normaliseHeader(k), k));
  return map;
}

function col(map: Map<string, string>, ...aliases: string[]): string | undefined {
  for (const a of aliases) {
    const found = map.get(a);
    if (found) return found;
  }
  return undefined;
}

export function parseStatus(raw: string): 'in stock' | 'checked out' | 'retired' | null {
  const n = normaliseHeader(raw);
  if (!n || n === 'instock') return 'in stock';
  if (n === 'checkedout' || n === 'checkout') return 'checked out';
  if (n === 'retired') return 'retired';
  return null;
}

export function parseCondition(raw: string): 'new' | 'used' | null {
  const n = normaliseHeader(raw);
  if (!n || n === 'new') return 'new';
  if (n === 'used') return 'used';
  return null;
}

export function parseTypesWorkbook(fileData: ArrayBuffer): { rows: ParsedTypeRow[] } | { error: string } {
  let raw: Record<string, unknown>[];
  try {
    raw = readSheetRows(fileData);
  } catch {
    return { error: 'Failed to read the file. Make sure it is a valid .xlsx or .xls file.' };
  }
  if (raw.length === 0) return { error: 'The file appears to be empty.' };
  if (raw.length > MAX_IMPORT_ROWS) {
    return { error: `This file has ${raw.length} rows. Please import at most ${MAX_IMPORT_ROWS} at a time.` };
  }

  const map = headerMapFromRow(raw[0]);
  const nameCol = col(map, 'itemname', 'name');
  if (!nameCol) return { error: 'Could not find an "Item Name" column. Please use the template.' };
  const skuCol = col(map, 'sku');
  const categoryCol = col(map, 'category');
  const notesCol = col(map, 'notes', 'note');

  const rows = raw
    .map(r => ({
      name: cell(r[nameCol]),
      sku: skuCol ? emptyToNull(cell(r[skuCol])) : null,
      category: categoryCol ? emptyToNull(cell(r[categoryCol])) : null,
      notes: notesCol ? emptyToNull(cell(r[notesCol])) : null,
    }))
    .filter(r => r.name.length > 0);

  if (rows.length === 0) return { error: 'No valid rows found (Item Name column is empty).' };
  return { rows };
}

export function parseUnitsWorkbook(fileData: ArrayBuffer): { rows: ParsedUnitRow[] } | { error: string } {
  let raw: Record<string, unknown>[];
  try {
    raw = readSheetRows(fileData);
  } catch {
    return { error: 'Failed to read the file. Make sure it is a valid .xlsx or .xls file.' };
  }
  if (raw.length === 0) return { error: 'The file appears to be empty.' };
  if (raw.length > MAX_IMPORT_ROWS) {
    return { error: `This file has ${raw.length} rows. Please import at most ${MAX_IMPORT_ROWS} at a time.` };
  }

  const map = headerMapFromRow(raw[0]);
  const nameCol = col(map, 'itemname', 'name');
  if (!nameCol) return { error: 'Could not find an "Item Name" column. Please use the template.' };

  const skuCol = col(map, 'sku');
  const categoryCol = col(map, 'category');
  const serialCol = col(map, 'serialmac', 'serial', 'mac', 'serialnumber');
  const warehouseCol = col(map, 'warehouse');
  const areaCol = col(map, 'storagearea', 'area', 'storage');
  const conditionCol = col(map, 'newused', 'condition');
  const statusCol = col(map, 'status');
  const referenceCol = col(map, 'reference', 'ref');
  const ownerCol = col(map, 'owner');
  const notesCol = col(map, 'notes', 'note');

  const rows: ParsedUnitRow[] = [];
  raw.forEach((r, i) => {
    const name = cell(r[nameCol]);
    if (!name) return;
    rows.push({
      rowNumber: i + 2,
      name,
      sku: skuCol ? emptyToNull(cell(r[skuCol])) : null,
      category: categoryCol ? emptyToNull(cell(r[categoryCol])) : null,
      serialMac: serialCol ? emptyToNull(cell(r[serialCol])) : null,
      warehouse: warehouseCol ? emptyToNull(cell(r[warehouseCol])) : null,
      storageArea: areaCol ? emptyToNull(cell(r[areaCol])) : null,
      conditionRaw: conditionCol ? cell(r[conditionCol]) : '',
      statusRaw: statusCol ? cell(r[statusCol]) : '',
      reference: referenceCol ? emptyToNull(cell(r[referenceCol])) : null,
      owner: ownerCol ? emptyToNull(cell(r[ownerCol])) : null,
      notes: notesCol ? emptyToNull(cell(r[notesCol])) : null,
    });
  });

  if (rows.length === 0) return { error: 'No valid rows found (Item Name column is empty).' };
  return { rows };
}

export interface UnitPreviewContext {
  items: Item[];
  stockUnits: StockUnit[];
  warehouses: Warehouse[];
  storageAreasByWarehouse: Record<string, StorageArea[]>;
  defaultStorageAreaIdByWarehouse: Record<string, string | null>;
  owners: Owner[];
  fallbackWarehouseName: string | null;
}

export function buildUnitPreview(rows: ParsedUnitRow[], ctx: UnitPreviewContext): UnitImportPreview {
  const blocked: RowIssue[] = [];
  const warnings: RowIssue[] = [];
  const validRows: ParsedUnitRow[] = [];

  const itemsByName = new Map(ctx.items.map(i => [key(i.name), i]));
  const skuByItem = new Map(ctx.items.filter(i => i.sku).map(i => [key(i.sku!), i]));
  const warehousesByName = new Map(ctx.warehouses.map(w => [key(w.name), w]));
  const ownersByName = new Map(ctx.owners.map(o => [key(o.name), o.name]));
  const existingSerials = new Set(
    ctx.stockUnits.map(u => u.serialMac?.trim().toLowerCase()).filter((s): s is string => !!s),
  );
  const fileSerials = new Map<string, number>();

  const plannedItems = new Map<string, { name: string; sku: string | null; existing: Item | null }>();
  const plannedWarehouses = new Set<string>();
  const plannedAreas = new Map<string, { warehouse: string; area: string }>();
  const plannedOwners = new Set<string>();
  const reused = new Set<string>();
  const discontinued = new Set<string>();

  if (!ctx.fallbackWarehouseName && ctx.warehouses.length === 0) {
    return {
      validRows: [],
      blocked: [{ rowNumber: 0, name: '', message: 'No warehouse is selected. Create a warehouse before importing units.' }],
      warnings: [],
      sampleRows: [],
      summary: {
        unitsToCreate: 0,
        newItemTypes: [],
        reusedItemTypes: [],
        discontinuedItemTypes: [],
        newWarehouses: [],
        newStorageAreas: [],
        newOwners: [],
      },
    };
  }

  for (const row of rows) {
    const status = parseStatus(row.statusRaw);
    if (status == null) {
      blocked.push({ rowNumber: row.rowNumber, name: row.name, message: `Unrecognised status "${row.statusRaw}". Use in stock, checked out, or retired.` });
      continue;
    }
    const condition = parseCondition(row.conditionRaw);
    if (condition == null) {
      blocked.push({ rowNumber: row.rowNumber, name: row.name, message: `Unrecognised condition "${row.conditionRaw}". Use new or used.` });
      continue;
    }
    if (status === 'checked out' && !row.owner) {
      blocked.push({ rowNumber: row.rowNumber, name: row.name, message: 'Checked out units require an Owner.' });
      continue;
    }

    const warehouseName = row.warehouse || ctx.fallbackWarehouseName;
    if (!warehouseName) {
      blocked.push({ rowNumber: row.rowNumber, name: row.name, message: 'No warehouse on this row and no warehouse is selected.' });
      continue;
    }

    const nameK = key(row.name);
    const existing = itemsByName.get(nameK) ?? null;
    const planned = plannedItems.get(nameK);

    if (existing) {
      if (row.sku && key(row.sku) !== key(existing.sku ?? '')) {
        blocked.push({
          rowNumber: row.rowNumber,
          name: row.name,
          message: `SKU "${row.sku}" does not match existing item SKU "${existing.sku ?? '(none)'}". Name and SKU cannot be changed this way.`,
        });
        continue;
      }
      reused.add(existing.name);
      if (existing.isDiscontinued) discontinued.add(existing.name);
    } else if (planned) {
      if (row.sku && key(row.sku) !== key(planned.sku ?? '')) {
        blocked.push({
          rowNumber: row.rowNumber,
          name: row.name,
          message: `SKU "${row.sku}" conflicts with SKU "${planned.sku ?? '(none)'}" already used for this item in the file.`,
        });
        continue;
      }
    } else if (row.sku && skuByItem.has(key(row.sku))) {
      const other = skuByItem.get(key(row.sku))!;
      blocked.push({
        rowNumber: row.rowNumber,
        name: row.name,
        message: `SKU "${row.sku}" is already used by "${other.name}".`,
      });
      continue;
    } else {
      const skuTakenInFile = valuesOf(plannedItems).some(p => p.sku && row.sku && key(p.sku) === key(row.sku));
      if (skuTakenInFile) {
        blocked.push({ rowNumber: row.rowNumber, name: row.name, message: `SKU "${row.sku}" is used by another new item in this file.` });
        continue;
      }
      plannedItems.set(nameK, { name: row.name, sku: row.sku, existing: null });
    }

    if (!warehousesByName.has(key(warehouseName))) plannedWarehouses.add(warehouseName);

    if (row.storageArea) {
      const wh = warehousesByName.get(key(warehouseName));
      const areas = wh ? (ctx.storageAreasByWarehouse[wh.id] ?? []) : [];
      const areaExists = areas.some(a => key(a.name) === key(row.storageArea!));
      if (!areaExists) {
        plannedAreas.set(`${key(warehouseName)}|${key(row.storageArea)}`, { warehouse: warehouseName, area: row.storageArea });
      }
    }

    if (row.owner && !ownersByName.has(key(row.owner))) plannedOwners.add(row.owner);

    if (row.serialMac) {
      const sk = key(row.serialMac);
      if (existingSerials.has(sk) || fileSerials.has(sk)) {
        warnings.push({
          rowNumber: row.rowNumber,
          name: row.name,
          message: `Serial/MAC "${row.serialMac}" is duplicated ${existingSerials.has(sk) ? 'in existing stock' : 'in this file'}.`,
        });
      }
      fileSerials.set(sk, row.rowNumber);
    }

    validRows.push(row);
  }

  const newItemTypes = valuesOf(plannedItems).filter(p => !p.existing).map(p => p.name);

  return {
    validRows,
    blocked,
    warnings,
    sampleRows: validRows.slice(0, 20),
    summary: {
      unitsToCreate: validRows.length,
      newItemTypes,
      reusedItemTypes: setValues(reused),
      discontinuedItemTypes: setValues(discontinued),
      newWarehouses: setValues(plannedWarehouses),
      newStorageAreas: valuesOf(plannedAreas),
      newOwners: setValues(plannedOwners),
    },
  };
}

export async function runTypesImport(
  rows: ParsedTypeRow[],
  mode: TypesImportMode,
  duplicateAction: DuplicateAction,
): Promise<TypesImportResult> {
  const res: TypesImportResult = { added: 0, updated: 0, skipped: 0, errors: [] };
  const inv = useInventoryStore.getState();

  try {
    if (mode === 'replace') {
      const { error } = await supabase.from('items').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      if (error) res.errors.push(`Failed to clear items: ${error.message}`);
    }

    await inv.fetchItems();
    const currentItems = mode === 'replace' ? [] : useInventoryStore.getState().items;
    const existingByKey = new Map(currentItems.map(i => [`${key(i.name)}|${key(i.sku ?? '')}`, i]));

    for (const row of rows) {
      try {
        const rowKey = `${key(row.name)}|${key(row.sku ?? '')}`;
        const existing = existingByKey.get(rowKey);

        if (existing && mode === 'add') {
          if (duplicateAction === 'skip') {
            res.skipped++;
            continue;
          }
          if (duplicateAction === 'update') {
            await useInventoryStore.getState().updateItem(existing.id, { category: row.category, notes: row.notes });
            res.updated++;
            continue;
          }
        }

        const newItem = await useInventoryStore.getState().addItem({
          name: row.name,
          sku: row.sku,
          category: row.category,
          notes: row.notes,
          requiresSerial: false,
          requiresMac: false,
        });
        if (newItem) {
          res.added++;
          existingByKey.set(rowKey, newItem);
        } else {
          res.errors.push(`Failed to add: ${row.name}`);
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'unknown error';
        res.errors.push(`${row.name}: ${message}`);
      }
    }
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'unknown error';
    res.errors.push(`Import failed: ${message}`);
  }

  return res;
}

function fallbackAreaId(
  warehouseId: string,
  ctx: {
    storageAreasByWarehouse: Record<string, StorageArea[]>;
    defaultStorageAreaIdByWarehouse: Record<string, string | null>;
  },
): string | null {
  const def = ctx.defaultStorageAreaIdByWarehouse[warehouseId];
  if (def) return def;
  return ctx.storageAreasByWarehouse[warehouseId]?.[0]?.id ?? null;
}

export async function runUnitsImport(
  rows: ParsedUnitRow[],
  fallbackWarehouseName: string | null,
): Promise<UnitsImportResult> {
  const res: UnitsImportResult = {
    itemsCreated: 0,
    warehousesCreated: 0,
    areasCreated: 0,
    ownersCreated: 0,
    unitsCreated: 0,
    skipped: 0,
    errors: [],
  };

  await useInventoryStore.getState().fetchItems();
  await useOwnersStore.getState().fetchOwners();

  const preview = buildUnitPreview(rows, {
    items: useInventoryStore.getState().items,
    stockUnits: useInventoryStore.getState().stockUnits,
    warehouses: useWarehouseStore.getState().warehouses,
    storageAreasByWarehouse: useWarehouseStore.getState().storageAreasByWarehouse,
    defaultStorageAreaIdByWarehouse: useWarehouseStore.getState().defaultStorageAreaIdByWarehouse,
    owners: useOwnersStore.getState().owners,
    fallbackWarehouseName,
  });

  for (const issue of preview.blocked) {
    res.errors.push(issue.rowNumber ? `Row ${issue.rowNumber} (${issue.name}): ${issue.message}` : issue.message);
    res.skipped++;
  }

  const itemsByName = new Map(useInventoryStore.getState().items.map(i => [key(i.name), i]));
  const warehousesByName = new Map(useWarehouseStore.getState().warehouses.map(w => [key(w.name), w]));
  const ownersByName = new Map(useOwnersStore.getState().owners.map(o => [key(o.name), o]));

  const resolveWarehouse = async (name: string): Promise<Warehouse | null> => {
    const existing = warehousesByName.get(key(name));
    if (existing) return existing;
    const created = await useWarehouseStore.getState().addWarehouse(name);
    if (!created) return null;
    warehousesByName.set(key(created.name), created);
    res.warehousesCreated++;
    return created;
  };

  const resolveArea = async (warehouse: Warehouse, areaName: string | null): Promise<string | null> => {
    const whState = useWarehouseStore.getState();
    if (!areaName) return fallbackAreaId(warehouse.id, whState);
    const areas = whState.storageAreasByWarehouse[warehouse.id] ?? [];
    const existing = areas.find(a => key(a.name) === key(areaName));
    if (existing) return existing.id;
    const created = await useWarehouseStore.getState().addStorageArea(warehouse.id, areaName);
    if (!created) return null;
    res.areasCreated++;
    return created.id;
  };

  const resolveOwner = async (name: string | null): Promise<string | null> => {
    if (!name) return null;
    const existing = ownersByName.get(key(name));
    if (existing) return existing.name;
    const created = await useOwnersStore.getState().addOwner(name);
    if (!created) return null;
    ownersByName.set(key(created.name), created);
    res.ownersCreated++;
    return created.name;
  };

  const resolveItem = async (row: ParsedUnitRow): Promise<Item | null> => {
    const existing = itemsByName.get(key(row.name));
    if (existing) return existing;
    const created = await useInventoryStore.getState().addItem({
      name: row.name,
      sku: row.sku,
      category: row.category,
      notes: null,
      requiresSerial: false,
      requiresMac: false,
    });
    if (!created) return null;
    itemsByName.set(key(created.name), created);
    res.itemsCreated++;
    return created;
  };

  const unitsToCreate: NewStockUnit[] = [];

  for (const row of preview.validRows) {
    try {
      const status = parseStatus(row.statusRaw)!;
      const condition = parseCondition(row.conditionRaw)!;
      const warehouseName = row.warehouse || fallbackWarehouseName;
      if (!warehouseName) {
        res.errors.push(`Row ${row.rowNumber} (${row.name}): No warehouse.`);
        res.skipped++;
        continue;
      }

      const warehouse = await resolveWarehouse(warehouseName);
      if (!warehouse) {
        res.errors.push(`Row ${row.rowNumber} (${row.name}): Failed to create warehouse "${warehouseName}".`);
        res.skipped++;
        continue;
      }

      const storageAreaId = await resolveArea(warehouse, row.storageArea);
      if (row.storageArea && storageAreaId == null) {
        res.errors.push(`Row ${row.rowNumber} (${row.name}): Failed to create storage area "${row.storageArea}".`);
        res.skipped++;
        continue;
      }

      const ownerName = await resolveOwner(row.owner);
      if (row.owner && !ownerName) {
        res.errors.push(`Row ${row.rowNumber} (${row.name}): Failed to create owner "${row.owner}".`);
        res.skipped++;
        continue;
      }

      const item = await resolveItem(row);
      if (!item) {
        res.errors.push(`Row ${row.rowNumber} (${row.name}): Failed to create item type.`);
        res.skipped++;
        continue;
      }

      unitsToCreate.push({
        itemId: item.id,
        warehouseId: warehouse.id,
        storageAreaId,
        serialMac: row.serialMac,
        condition,
        status,
        reference: row.reference,
        owner: ownerName,
        notes: row.notes,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'unknown error';
      res.errors.push(`Row ${row.rowNumber} (${row.name}): ${message}`);
      res.skipped++;
    }
  }

  for (let i = 0; i < unitsToCreate.length; i += UNIT_CHUNK_SIZE) {
    const chunk = unitsToCreate.slice(i, i + UNIT_CHUNK_SIZE);
    const created = await useInventoryStore.getState().addStockUnits(chunk, 'Imported from Excel');
    res.unitsCreated += created.length;
    if (created.length !== chunk.length) {
      res.errors.push(`Only ${created.length} of ${chunk.length} units were created in a batch starting at unit ${i + 1}.`);
    }
  }

  return res;
}
